import React, { useState } from 'react'
import {
  Box, Dialog, DialogContent, DialogTitle, Divider,
  IconButton, List, ListItem, ListItemText, Tooltip, Typography, makeStyles, useTheme
} from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import TruncCell from './TruncCell'

const useStyles = makeStyles((theme) => ({
  idBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1px 7px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main + '14',
    marginLeft: 6,
    '&:hover': { backgroundColor: theme.palette.primary.main + '28' }
  },
  idText: {
    fontFamily: 'monospace',
    fontSize: 12,
    color: theme.palette.text.primary
  }
}))

function CopyableIdRow({ id, idx, isLast }) {
  const theme = useTheme()
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(id).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
    }
  }

  return (
    <ListItem
      disableGutters
      style={{ borderBottom: isLast ? 'none' : `1px solid ${theme.palette.divider}`, padding: '6px 0' }}
    >
      <ListItemText
        primary={
          <Box display='flex' alignItems='center' justifyContent='space-between' style={{ gap: 8 }}>
            <Box display='flex' alignItems='center' style={{ gap: 10, minWidth: 0 }}>
              <Typography variant='caption' style={{ minWidth: 22, color: theme.palette.text.disabled, fontWeight: 600, flexShrink: 0 }}>
                {idx + 1}.
              </Typography>
              <Typography style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                {id}
              </Typography>
            </Box>
            <Tooltip title={copied ? 'Copied!' : 'Copy'} placement='top'>
              <IconButton
                size='small'
                onClick={handleCopy}
                style={{ flexShrink: 0, color: copied ? theme.palette.success.main : theme.palette.text.disabled }}
              >
                <FontAwesomeIcon icon={['fal', copied ? 'check' : 'copy']} style={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
    </ListItem>
  )
}

function ManifestIdsCell({ ids }) {
  const classes = useStyles()
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const [allCopied, setAllCopied] = useState(false)
  const list = Array.isArray(ids) ? ids : []

  if (list.length === 0) {
    return <span style={{ color: theme.palette.text.disabled }}>—</span>
  }

  if (list.length === 1) {
    return <TruncCell value={list[0]} />
  }

  const handleCopyAll = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(list.join('\n')).then(() => {
        setAllCopied(true)
        setTimeout(() => setAllCopied(false), 1500)
      })
    }
  }

  return (
    <>
      <Box display='flex' alignItems='center'>
        <Tooltip title={list[0]}>
          <span
            className={classes.idText}
            style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}
          >
            {list[0]}
          </span>
        </Tooltip>
        <span className={classes.idBadge} onClick={() => setOpen(true)}>
          +{list.length - 1} more
        </span>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Box display='flex' alignItems='center' style={{ gap: 8 }}>
              <FontAwesomeIcon icon={['fal', 'list']} style={{ fontSize: 14 }} />
              <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
                Master/House Bill System IDs
              </Typography>
              <span style={{
                padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                backgroundColor: theme.palette.primary.main + '20',
                color: theme.palette.primary.main
              }}>
                {list.length}
              </span>
            </Box>
            <Box display='flex' alignItems='center' style={{ gap: 4 }}>
              <Tooltip title={allCopied ? 'All copied!' : 'Copy all'} placement='top'>
                <IconButton
                  size='small'
                  onClick={handleCopyAll}
                  style={{ color: allCopied ? theme.palette.success.main : theme.palette.text.secondary }}
                >
                  <FontAwesomeIcon icon={['fal', allCopied ? 'check' : 'copy']} style={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
              <IconButton size='small' onClick={() => setOpen(false)}>
                <FontAwesomeIcon icon={['fal', 'xmark']} style={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent style={{ padding: '8px 24px 16px' }}>
          <List dense disablePadding>
            {list.map((id, idx) => (
              <CopyableIdRow
                key={id}
                id={id}
                idx={idx}
                isLast={idx === list.length - 1}
              />
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ManifestIdsCell
